'use client';

import { Helmet } from 'react-helmet-async';
import {
  getPageSEO,
  type PageSEO as PageSEOType,
} from '@trashlab/sitesapp/src/organizations/redebins/template/seo-config';

interface PageSEOProps {
  /**
   * The pathname of the current page (e.g., '/contact', '/faq')
   * This is used to look up page-specific SEO from the config
   */
  pathname: string;
}

/**
 * PageSEO Component - Injects page-specific SEO meta tags via React Helmet
 *
 * This component overrides the default template SEO with page-specific
 * title, description, and keywords. It should be included at the top
 * of each page's content component.
 *
 * SEO data is sourced from seo-config.ts which maps pathnames to SEO data.
 *
 * Usage:
 * ```tsx
 * export const MyPageContent = () => {
 *   return (
 *     <>
 *       <PageSEO pathname="/my-page" />
 *       <section>...</section>
 *     </>
 *   );
 * };
 * ```
 */
export const PageSEO = ({ pathname }: PageSEOProps) => {
  const seo = getPageSEO(pathname);

  // If no page-specific SEO is configured, don't render anything
  // The template's default SEO will be used instead
  if (!seo) {
    return null;
  }

  // Build canonical URL
  const canonicalUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `https://mn.redebins.com${pathname}`;

  return (
    <Helmet>
      {/* Primary Meta Tags - Override template defaults */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords && seo.keywords.length > 0 && (
        <meta name="keywords" content={seo.keywords.join(', ')} />
      )}

      {/* Open Graph / Facebook - Override template defaults */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonicalUrl} />

      {/* Twitter - Override template defaults */}
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
};
